import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/shared/query/client';
import { RouterProvider } from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { router } from './router';
import { useAuth } from './shared/hooks/useAuth';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { installGlobalErrorHandlers } from './shared/services/telemetry';
import { initObservability } from './shared/services/observability';
import { captureReferral } from './shared/utils/referral';
import { captureUtm } from './shared/utils/utm';
import { installChunkReloadHandler } from './shared/utils/chunkReload';
import { applyWaitingUpdate, installSwUpdateWatcher } from './shared/utils/swUpdate';
import { initTheme } from './shared/utils/theme';
import { captureAuthLinkError } from './shared/auth/linkError';
import { capturePushLanding } from './shared/push/pushLanding';
import { stopFocusZoom } from './shared/pwa/noZoom';
import { installSectionWords } from './shared/utils/sectionWords';
import { captureInstallPrompt } from './shared/pwa/installFirst';
import { followPlaceRelease } from './shared/release/olderBuild';
import './styles/globals.css';

// yourplace yp-4: a place one release behind opens on the build that matches it.
// No call; it reads only what the directory stored at login (none until yp-5).
const leaving = followPlaceRelease();

// nozoom: a tap on a box never leaves an iPhone zoomed in (the pinch still works).
stopFocusZoom();

// sectionword: the screen says "Section 34", never "§34", whether the text came
// from our code, the database or an edge function. See sectionWords.ts.
installSectionWords();

// installfirst: Chrome offers the install once, early; keep it for the sign-up
// screen, which is lazy and may draw after the offer came.
captureInstallPrompt();

// Apply the saved theme (Light / Dark / System) and keep 'system' following the OS.
// The pre-paint class is already set by the inline bootstrap in index.html; this
// re-applies from the same key and wires the live OS-change listener.
initTheme();

// Capture a partner referral code (?ref=) and marketing UTM params on first
// touch, before any OAuth redirect can drop the query string. Both persist in
// localStorage and are attached to the signup funnel event during onboarding.
captureReferral();
captureUtm();

// A sign-in link that was already used (often by an email scanner) or has expired
// comes back to '/' with the reason in the URL hash. '/' is the MARKETING page for
// anyone without a session, so until this ran the person was silently shown the
// home page and told nothing. Stash the reason and clean the URL; the sign-in
// screen renders it. Only touches error redirects — a successful one carries the
// tokens and is left alone for auth-js. See shared/auth/linkError.ts.
captureAuthLinkError();

// webpush: a tap on a phone notification that opened Karr cold carries where
// it lands in `?karrpush=`. Keep it and clean the URL before the router reads
// it; AppLayout lands there once the person's businesses are known.
capturePushLanding();

// Capture async/uncaught failures that React error boundaries cannot see.
installGlobalErrorHandlers();
// Attach a crash-reporting provider when VITE_SENTRY_DSN is configured (no-op otherwise).
void initObservability();

// Auto-recover from stale code-split chunks after a new deploy — the #1 source of
// "Failed to fetch dynamically imported module". Covers Vite's preloadError plus the
// browser-specific error/rejection variants (Safari/Firefox don't fire preloadError),
// and reloads to the fresh build (bounded, so a broken chunk can't loop). The React
// error boundaries (RouteError / ErrorBoundary) show a calm "Updating…" for anything
// that still reaches render. See chunkReload.ts.
installChunkReloadHandler();

// Complement to the above: PROACTIVELY notice a new deploy. The autoUpdate SW only
// re-checks on navigation, so a tab / installed-PWA window left open serves last
// week's build indefinitely (users retest a fixed bug on old code). Re-check on an
// interval + on refocus, and reload once (with a toast) when the new SW takes over.
installSwUpdateWatcher();
// meetcs2: a new version held back while a form was open loads on the next
// page change, once nothing is left open (see swUpdate.ts).
router.subscribe('onResolved', () => applyWaitingUpdate());

// The app's one QueryClient lives in shared/query/client.ts, so entityChanged
// can tell it from outside React (addedonce).

export function App() {
  const initialize = useAuth((state) => state.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster
            position="bottom-right"
            richColors
            // Every pop-up carries its own ✕ — a toast you can only wait out
            // is furniture, not a message (founder, 2026-08-07).
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'var(--font-sans)',
              },
            }}
          />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

if (!leaving) createRoot(document.getElementById('root')!).render(<App />); 
